/**
 * PalPendantMapPage — V2 main orchestrator for the PalPendant Locator page.
 *
 * Full-screen Mapbox GL map with a floating side panel for searching,
 * pinging, and live-tracking pendant devices. Polls every 6 seconds for
 * updated location data.
 *
 * Replaces V1 PalPendantMap.jsx + pendant-map-assets/Map.jsx + LocationList.jsx.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    getCampusConfig,
    updateSessionCampus,
} from '../../../services/session.js';
import { iAmHigherAuthority } from '../../../libs.jsx';
import { fetchPendantLocations, pingPendantDevice } from './api/pendantMapApi.js';

import PendantMap from './components/PendantMap.jsx';
import LocationPanel from './components/LocationPanel.jsx';

import './palpendant-map-page.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const POLL_INTERVAL_MS = 6000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const PalPendantMapPage = () => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);

    const [campusId, setCampusId] = useState(() => getCampusId());

    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);

    const tzOffset = campusConfig?.tz_offset;
    const showCampusSelector = useMemo(
        () => iAmHigherAuthority(30, session?.role_weight),
        [session]
    );

    // -- Data --
    const [locations, setLocations] = useState([]);
    const [alertedDevices, setAlertedDevices] = useState([]);
    const [loaded, setLoaded] = useState(false);

    // -- Ping --
    const [pingingDevice, setPingingDevice] = useState(null);

    // -- Toast --
    const [toast, setToast] = useState(null);
    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // -- Map ref --
    const mapRef = useRef(null);

    // ======================================================================
    // Data fetching + polling
    // ======================================================================
    const fetchData = useCallback(async () => {
        try {
            const data = await fetchPendantLocations(campusId);
            if (data && data.length > 0) {
                setLocations(data[0]?.locations ? data[0].locations : []);
                setAlertedDevices(data[0]?.alerted ? data[0].alerted : []);
            }
            setLoaded(true);
        } catch (err) {
            console.error('[PalPendantMapPage] Fetch error:', err);
        }
    }, [campusId]);

    useEffect(() => {
        // Immediate first fetch
        fetchData();

        // Poll every 6 seconds
        const interval = setInterval(fetchData, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchData]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleLocationSelect = useCallback((location) => {
        if (mapRef.current) {
            mapRef.current.flyTo({
                center: [location.longitude, location.latitude],
                zoom: 15,
            });
            mapRef.current.selectMarker(location.manufacture_uuid);
        }
    }, []);

    const handlePingDevice = useCallback(async (deviceId) => {
        setPingingDevice(deviceId);
        try {
            const ok = await pingPendantDevice(deviceId, {
                campusId,
                userId: getUserId(),
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Device pinged successfully.');
            } else {
                showToast('Failed to ping device.', 'error');
            }
        } catch {
            showToast('Failed to ping device.', 'error');
        } finally {
            setPingingDevice(null);
        }
    }, [campusId, showToast]);

    const handleCampusChange = useCallback((newId) => {
        setCampusId(newId);
        updateSessionCampus(newId);
        setLocations([]);
        setAlertedDevices([]);
        setLoaded(false);
    }, []);

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="pm-page">
                    {/* Toast notification */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`}>
                            <i className={`fe ${toast.type === 'success' ? 'fe-check-circle' : 'fe-alert-circle'}`} />
                            {toast.message}
                            <button className="dp-toast__close" onClick={() => setToast(null)}>
                                <i className="fe fe-x" />
                            </button>
                        </div>
                    ) : null}

                    {/* Full-screen map */}
                    <PendantMap
                        ref={mapRef}
                        locations={locations}
                        alertedDevices={alertedDevices}
                        tzOffset={tzOffset}
                    />

                    {/* Floating panel */}
                    <LocationPanel
                        locations={locations}
                        alertedDevices={alertedDevices}
                        onLocationSelect={handleLocationSelect}
                        campusId={campusId}
                        onCampusChange={handleCampusChange}
                        campusList={campusList}
                        showCampusSelector={showCampusSelector}
                        onPingDevice={handlePingDevice}
                        pingingDevice={pingingDevice}
                        mapRef={mapRef}
                    />
                </div>
            </div>
        </>
    );
};

export default PalPendantMapPage;
